import { MaybeExt } from '@castoredc/castor_prose';
import type {
  Maybe,
  Value,
  Array as ProseArray,
} from '@castoredc/castor_prose';
import type { DbCalculationsData } from '@hub/common';

export const buildCalculationsDataPreview = (
  calculationsData: DbCalculationsData,
  locale = 'en-US'
) => {
  const record: Record<string, string> = {};

  // Extract calculation values and convert them to strings
  Object.entries(calculationsData?.values ?? {}).forEach(([key, value]) => {
    // Key format: "questionnaire_name.calculation_name"
    const parts = key.split('.');
    const calculationName = parts[parts.length - 1];

    if (!calculationName || !value.kind) {
      return;
    }

    const displayValue = (value: Value | undefined) => {
      if (value === undefined) {
        return 'not defined';
      }

      switch (value.kind?.$case) {
        case 'boolean':
        case 'float64':
        case 'str':
          return displayPrimitive(value.kind.value);
        case 'maybe':
          return displayMaybe(value.kind.value);
        case 'array':
          return displayArray(value.kind.value);
        default:
          return '(preview not supported yet)';
      }
    };

    const displayPrimitive = (
      primitiveValue: boolean | number | string
    ): string => {
      if (typeof primitiveValue === 'number') {
        return primitiveValue.toLocaleString(locale);
      }
      return String(primitiveValue);
    };

    const displayMaybe = (maybeValue: Maybe): string => {
      if (MaybeExt.is_missing(maybeValue)) {
        return 'no value';
      }

      return displayValue(MaybeExt.as(maybeValue));
    };

    const displayArray = (arrayValue: ProseArray): string => {
      switch (arrayValue.kind?.$case) {
        case 'float64':
        case 'boolean':
        case 'str':
          return `[${arrayValue.kind.value.items
            .map(x => displayPrimitive(x))
            .join(', ')}]`;
        case 'maybe':
          return `[${arrayValue.kind.value.items
            .map(x => displayMaybe(x))
            .join(', ')}]`;
        case 'array':
          return `[${arrayValue.kind.value.items
            .map(x => displayArray(x))
            .join(', ')}]`;
        default:
          return '(preview not supported yet)';
      }
    };

    record[calculationName] = displayValue(value);
  });

  return record;
};
